// What a boot opens: the bundled starter scene on a brand-new browser, the
// layout you edited last on every boot after that. Either way you land on a
// room rather than an empty grid (and the welcome card's "open the Models
// drawer" instruction finally has something behind it).
//
// The asset in public/ is the same `.tinydraftplan.json` shape downloadPlan
// writes — a SavedFloorPlan plus a bundled `models[]` — so this is deliberately
// the same registration readPlanFile does: validate, putModel every bundled
// model KEEPING ITS ID, then store the plan. The ids matter: placements
// reference models by id, and floor-mode's load silently SKIPS a placement
// whose model isn't in the collection, so a plan seeded without its models
// would open as an empty room with no error anywhere.
//
// Regenerate the asset with `node scripts/gen-starter-scene.mjs`.

import { listModels, putModel } from "../model/collection-store";
import { type SavedModel, isSavedModel } from "../model/saved-model";
import { loadSettings, saveSetting } from "../ui/settings-store";
import { type SavedFloorPlan, isSavedFloorPlan } from "./saved-floor-plan";
import { listPlans, putPlan } from "./floor-plan-store";

const ASSET = `${import.meta.env.BASE_URL}starter-scene.tinydraftplan.json`;

// What a boot should open, plus what it needs to render it.
export interface BootLayout {
  plan: SavedFloorPlan;
  // Models to prime floor-mode's in-session cache with. Normally empty — the
  // placements resolve from the collection. Non-empty when the scene was just
  // seeded, so it still renders even if storing the models failed.
  models: SavedModel[];
}

// A seed problem is never worth interrupting a user over — a missing example
// scene is a disappointment, not an error — but it must not be INVISIBLE
// either: swallowed silently, "no scene and nothing in Layouts" looks identical
// to a bad deploy and is undiagnosable from a bug report. So: warn, don't alert.
function warn(what: string, e: unknown): void {
  console.warn(`[starter-scene] ${what}:`, e);
}

// Seed the starter scene if this browser has never been offered it AND holds no
// documents of its own. Returns what to open, or null to leave the boot layout
// alone. Never throws — a thrown error here would abort the rest of boot.
//
// Deliberately tolerant of a DEAD STORE. Private browsing can block IndexedDB
// entirely, and a visitor poking at the app in a private window is exactly the
// audience the example scene exists for — so a store that can't be read is
// treated as an empty one, and a store that can't be written still gets the
// scene on screen (just not persisted). The flag is only set once the scene is
// actually IN the library, so a browser that can't persist gets it offered
// again next boot rather than losing it forever.
export async function seedStarterScene(): Promise<BootLayout | null> {
  if (loadSettings().starterSceneSeeded) return null;
  try {
    // Never seed over existing work. An upgrading user with documents already
    // in the pool gets the flag set and nothing else, so this check runs once
    // rather than on every boot forever. A store that throws reads as empty.
    const existing = await Promise.all([
      listPlans().catch((e) => {
        warn("could not read layouts (storage unavailable?)", e);
        return [];
      }),
      listModels().catch(() => []),
    ]);
    if (existing[0].length > 0 || existing[1].length > 0) {
      saveSetting("starterSceneSeeded", true);
      return null;
    }

    const res = await fetch(ASSET);
    if (!res.ok) {
      warn(`asset request failed (HTTP ${res.status})`, ASSET);
      return null; // leave the flag unset so a later boot retries
    }
    const parsed: unknown = await res.json();
    if (!isSavedFloorPlan(parsed)) {
      warn("asset failed validation", ASSET);
      return null;
    }

    const bundled = (parsed as { models?: unknown }).models;
    const models: SavedModel[] = Array.isArray(bundled)
      ? bundled.filter((m): m is SavedModel => isSavedModel(m))
      : [];
    // Store the plan without its bundle (kept light), and — unlike readPlanFile
    // — keep the asset's own plan id, so a seed that somehow ran twice
    // overwrites rather than leaving two copies of the example.
    const plan: SavedFloorPlan = { ...(parsed as SavedFloorPlan) };
    delete (plan as { models?: unknown }).models;

    let persisted = true;
    try {
      for (const m of models) await putModel(m);
      await putPlan(plan);
    } catch (e) {
      persisted = false;
      warn("could not save the example to this browser; showing it unsaved", e);
    }
    // Only claim it's been seeded once it's really in the library.
    if (persisted) saveSetting("starterSceneSeeded", true);
    return { plan, models };
  } catch (e) {
    warn("seeding failed", e);
    return null;
  }
}

// The most recently touched layout — what "reopen where I left off" means.
// `updatedAt` is stamped on every save (and by a sync pull), so this is the
// last one edited on any of the user's devices. Missing timestamps sort last
// rather than winning by accident. Pure, so the choice is unit-testable.
export function pickMostRecentPlan(plans: readonly SavedFloorPlan[]): SavedFloorPlan | null {
  let best: SavedFloorPlan | null = null;
  for (const p of plans) {
    const t = Number.isFinite(p.updatedAt) ? p.updatedAt : -Infinity;
    const bt = best && Number.isFinite(best.updatedAt) ? best.updatedAt : -Infinity;
    if (!best || t > bt) best = p;
  }
  return best;
}

// The layout a boot should open: the freshly seeded starter scene on a new
// browser, otherwise the last one edited. Null means "nothing to open" — leave
// the blank layout alone. Reads the pool for whatever scope is active WHEN IT
// RUNS, so callers must wait for auth to settle first (see auth/session's
// authSettled) or they'll pick from the anonymous pool and get overwritten.
export async function pickBootLayout(): Promise<BootLayout | null> {
  const seeded = await seedStarterScene();
  if (seeded) return seeded;
  try {
    const plan = pickMostRecentPlan(await listPlans());
    // Its models come from the collection, so nothing to prime the cache with.
    return plan ? { plan, models: [] } : null;
  } catch (e) {
    warn("could not read layouts to reopen the last one", e);
    return null;
  }
}
